import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowRight, ArrowLeft, Check, MessageCircle, Phone, Globe, CalendarClock,
  ShoppingBag, Bot, Users, Sparkles, Layers, Send, Star, Shield, Wrench,
  Crown, Briefcase, Heart, MapPin, Share2, Gift, CheckCircle2,
  FileText, BellRing, Headphones, BarChart3, UserCog, Search, Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import {
  createLead, formatErr, getConfig, session, vendorCheck,
  createCheckoutSession,
} from "../lib/api";

const IMPROVE = [
  { value: "Conseguir más clientes", icon: Users },
  { value: "Organizar citas", icon: CalendarClock },
  { value: "Vender productos", icon: ShoppingBag },
  { value: "Mejorar presencia online", icon: Sparkles },
];
const CONTACT = [
  { value: "WhatsApp", icon: MessageCircle },
  { value: "Teléfono", icon: Phone },
  { value: "Redes sociales", icon: Share2 },
  { value: "Página web", icon: Globe },
  { value: "Ubicación (Google Maps)", icon: MapPin },
  { value: "Otro", icon: Layers },
];
const IMPLEMENT = [
  { value: "Botón directo a WhatsApp", icon: MessageCircle },
  { value: "Formularios de contacto", icon: FileText },
  { value: "Integración con Google Maps", icon: MapPin },
  { value: "Sistema de reservas y citas online", icon: CalendarClock },
  { value: "Respuestas automáticas", icon: Bot },
  { value: "Recordatorios automáticos", icon: BellRing },
  { value: "Asistentes virtuales", icon: Headphones, advanced: true },
  { value: "Métricas y analítica de clientes", icon: BarChart3 },
  { value: "Gestión y seguimiento de clientes", icon: UserCog },
  { value: "Optimización SEO", icon: Search },
  { value: "Mejora y gestión de reseñas", icon: Star, advanced: true },
  { value: "Tienda Virtual", icon: ShoppingBag, advanced: true },
  { value: "App Móvil", icon: Smartphone, advanced: true },
];
const STYLES = [
  { value: "Elegante", icon: Crown },
  { value: "Moderno", icon: Sparkles },
  { value: "Cercano", icon: Heart },
  { value: "Profesional", icon: Briefcase },
  { value: "Otro", icon: Layers },
];

const PLANS = [
  {
    id: "gratuito",
    name: "Gratuito",
    tagline: "Solo prototipo",
    price: "0€",
    priceNote: "Sin pago",
    features: ["Recibe un prototipo personalizado", "Sin compromiso", "Upgrade cuando quieras"],
  },
  {
    id: "profesional",
    name: "Profesional",
    tagline: "El más elegido",
    price: "150€",
    priceNote: "75€ ahora · 75€ al entregar",
    recommended: true,
    features: [
      "Web completa y rápida",
      "Optimización para captar clientes",
      "Botón WhatsApp directo",
      "Revisión y mantenimiento trimestral",
    ],
  },
  {
    id: "avanzado",
    name: "Avanzado",
    tagline: "Mejor experiencia",
    price: "300€",
    priceNote: "150€ ahora · 150€ al entregar",
    features: [
      "Web + sistema de citas integrado",
      "Optimización avanzada UX",
      "Recordatorios automáticos",
      "Revisión y mantenimiento trimestral",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    tagline: "Solución completa",
    price: "500€",
    priceNote: "250€ ahora · 250€ al entregar",
    features: [
      "Web completa + sistema de citas",
      "Respuestas automáticas",
      "Tienda online integrada",
      "Solución completa de negocio",
    ],
  },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [vendorValid, setVendorValid] = useState(null);
  const [vendorFromRef, setVendorFromRef] = useState(false);
  const [data, setData] = useState({
    business_type: "",
    style: "",
    style_other: "",
    improve: [],
    current_contact: "",
    implement: [],
    selected_plan: "profesional",
    contact_name: "",
    email: "",
    phone: "",
    vendor_code: "",
    terms_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState({ whatsapp: "" });

  useEffect(() => { getConfig().then(setConfig).catch(() => {}); }, []);

  // Read ?ref= from URL or sessionStorage to auto-link vendor
  useEffect(() => {
    const fromUrl = (searchParams.get("ref") || "").toUpperCase().trim();
    const fromStorage = (sessionStorage.getItem("el_ref") || "").toUpperCase().trim();
    const ref = fromUrl || fromStorage;
    if (!ref) return;
    if (fromUrl) sessionStorage.setItem("el_ref", fromUrl);
    setData((d) => ({ ...d, vendor_code: ref }));
    (async () => {
      try {
        const r = await vendorCheck(ref);
        if (r.valid) {
          setVendorValid({ valid: true, name: r.name });
          setVendorFromRef(true);
        } else {
          setVendorValid({ valid: false });
          sessionStorage.removeItem("el_ref");
        }
      } catch {
        setVendorValid({ valid: false });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === 6) { // loading
      const t = setTimeout(() => setStep(7), 1800);
      return () => clearTimeout(t);
    }
  }, [step]);

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const toggle = (key, value) =>
    setData((d) => ({
      ...d,
      [key]: d[key].includes(value) ? d[key].filter((v) => v !== value) : [...d[key], value],
    }));

  const checkVendor = async (code) => {
    if (!code) { setVendorValid(null); return; }
    try {
      const r = await vendorCheck(code.trim());
      setVendorValid(r.valid ? { valid: true, name: r.name } : { valid: false });
    } catch { setVendorValid({ valid: false }); }
  };

  const submit = async (withPayment) => {
    if (!data.contact_name || !data.email) {
      toast.error("Completa nombre y email");
      return;
    }
    if (!data.terms_accepted) {
      toast.error("Debes aceptar la política de privacidad");
      return;
    }
    const isPaid = data.selected_plan && data.selected_plan !== "gratuito";
    // Enforce: paid plans must go through Stripe
    if (isPaid && !withPayment) {
      toast.error("Para este plan debes completar el pago");
      return;
    }
    setSubmitting(true);
    try {
      const lead = await createLead({
        contact_name: data.contact_name,
        email: data.email,
        phone: data.phone || undefined,
        business_type: data.business_type || "—",
        improve: data.improve,
        current_contact: data.current_contact || "—",
        implement: data.implement,
        style: data.style || undefined,
        style_other: data.style === "Otro" ? data.style_other : undefined,
        selected_plan: data.selected_plan,
        vendor_code: data.vendor_code ? data.vendor_code.toUpperCase() : undefined,
        terms_accepted: data.terms_accepted === true,
      });
      session.save(lead);

      if (withPayment && data.selected_plan !== "gratuito") {
        // Stripe checkout
        const checkout = await createCheckoutSession(
          lead.id,
          data.selected_plan,
          window.location.origin
        );
        window.location.href = checkout.url;
        return;
      }
      toast.success("¡Listo! Te contactaremos pronto");
      navigate("/panel");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  const wa = (config.whatsapp || "").replace(/\D/g, "");
  const selectedPlan = PLANS.find((p) => p.id === data.selected_plan) || PLANS[1];
  const waUrl = wa
    ? `https://wa.me/${wa}?text=${encodeURIComponent(
        `Hola, me interesa el plan ${selectedPlan.name} para mi ${data.business_type || "negocio"}.`
      )}`
    : "#";

  const canNext = useMemo(() => {
    if (step === 1) return data.business_type.trim().length > 1;
    if (step === 2) return !!data.style && (data.style !== "Otro" || data.style_other.trim().length > 0);
    if (step === 3) return data.improve.length > 0;
    if (step === 4) return !!data.current_contact;
    if (step === 5) return data.implement.length > 0;
    return true;
  }, [step, data]);

  // Visible progress bar: steps 1..5, 7, 8, 9 (excluding 0 welcome, 6 loading)
  const VISIBLE_TOTAL = 8;
  const visibleIndex = (() => {
    if (step >= 1 && step <= 5) return step;
    if (step === 7) return 6;
    if (step === 8) return 7;
    if (step === 9) return 8;
    return 0;
  })();
  const showProgress = visibleIndex > 0;

  return (
    <div className="wizard-shell" data-testid="onboarding-wizard">
      <div className="wizard-frame">
        <header className="px-5 pt-5 pb-3 flex items-center gap-3">
          {step > 0 && step !== 6 ? (
            <button
              onClick={back}
              className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
              data-testid="btn-back-step" aria-label="Atrás"
            ><ArrowLeft className="w-5 h-5 text-slate-700" /></button>
          ) : (
            <button
              onClick={() => navigate("/")}
              className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
              data-testid="btn-exit-onboarding" aria-label="Salir"
            ><ArrowLeft className="w-5 h-5 text-slate-700" /></button>
          )}
          <div className="flex-1 flex items-center justify-center"><Logo size={28} withWordmark /></div>
          <div className="w-9" />
        </header>

        {showProgress && (
          <div className="px-5 pb-2" data-testid="wizard-progress">
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-[var(--el-red)] transition-all duration-500 ease-out"
                style={{ width: `${(visibleIndex / VISIBLE_TOTAL) * 100}%` }} />
            </div>
            <div className="text-[10px] text-slate-400 mt-1.5 tracking-[0.18em] uppercase">
              Paso {visibleIndex} de {VISIBLE_TOTAL}
            </div>
          </div>
        )}

        <main className="flex-1 flex flex-col px-5 pt-3 pb-6 overflow-hidden" key={step}>
          <div className="flex-1 flex flex-col step-anim min-h-0">
            {step === 0 && (
              <CenteredStep
                eyebrow="Empezamos"
                title="Vamos a crear la mejor solución para tu negocio"
                subtitle="En pocos pasos te mostramos cómo conseguir más clientes."
                cta="Continuar" onCta={next} ctaTestId="btn-step0-continue"
                icon={<Sparkles className="w-7 h-7 text-[var(--el-red)]" />}
              />
            )}

            {step === 1 && (
              <QuestionStep eyebrow="Pregunta 1 de 5" title="¿Qué tipo de negocio tienes?"
                subtitle="Cuéntanos a qué te dedicas."
                cta="Siguiente" disabled={!canNext} onCta={next} ctaTestId="btn-step1-next">
                <Input autoFocus value={data.business_type}
                  onChange={(e) => setData({ ...data, business_type: e.target.value })}
                  placeholder="Ej. Peluquería, restaurante, tienda..."
                  className="h-14 text-lg rounded-2xl" data-testid="input-business-type" />
              </QuestionStep>
            )}

            {step === 2 && (
              <QuestionStep eyebrow="Pregunta 2 de 5" title="¿Qué estilo quieres?"
                subtitle="Elige el estilo que mejor define tu marca."
                cta="Siguiente" disabled={!canNext} onCta={next} ctaTestId="btn-step2-next">
                <OptionGrid options={STYLES}
                  selected={data.style ? [data.style] : []}
                  onSelect={(v) => setData({ ...data, style: data.style === v ? "" : v })}
                  testidPrefix="opt-style" />
                {data.style === "Otro" && (
                  <Input value={data.style_other} autoFocus
                    onChange={(e) => setData({ ...data, style_other: e.target.value })}
                    placeholder="Describe tu estilo"
                    className="h-12 rounded-2xl mt-2" data-testid="input-style-other" />
                )}
              </QuestionStep>
            )}

            {step === 3 && (
              <QuestionStep eyebrow="Pregunta 3 de 5" title="¿Qué quieres mejorar?"
                subtitle="Puedes elegir más de una opción."
                cta="Siguiente" disabled={!canNext} onCta={next} ctaTestId="btn-step3-next">
                <OptionGrid options={IMPROVE} selected={data.improve} multi
                  onSelect={(v) => toggle("improve", v)} testidPrefix="opt-improve" />
              </QuestionStep>
            )}

            {step === 4 && (
              <QuestionStep eyebrow="Pregunta 4 de 5" title="¿Cómo te contactan actualmente?"
                subtitle="Selecciona el canal principal."
                cta="Siguiente" disabled={!canNext} onCta={next} ctaTestId="btn-step4-next">
                <OptionGrid options={CONTACT}
                  selected={data.current_contact ? [data.current_contact] : []}
                  onSelect={(v) => setData({ ...data, current_contact: data.current_contact === v ? "" : v })}
                  testidPrefix="opt-contact" />
              </QuestionStep>
            )}

            {step === 5 && (
              <QuestionStep eyebrow="Pregunta 5 de 5" title="¿Qué te gustaría implementar?"
                subtitle="Esto nos ayuda a recomendarte el plan ideal."
                cta="Crear mi solución" disabled={!canNext} onCta={() => setStep(6)}
                ctaTestId="btn-step5-next">
                <OptionGrid options={IMPLEMENT} selected={data.implement} multi
                  onSelect={(v) => toggle("implement", v)} testidPrefix="opt-implement" />
              </QuestionStep>
            )}

            {step === 6 && <LoadingStep />}

            {step === 7 && <ResultStep data={data} onContinue={next} />}

            {step === 8 && (
              <PlansStep selected={data.selected_plan}
                onSelect={(id) => setData({ ...data, selected_plan: id })}
                onContinue={next} />
            )}

            {step === 9 && (
              <FinalStep data={data} setData={setData}
                vendorValid={vendorValid} checkVendor={checkVendor}
                submitting={submitting}
                onSubmit={submit} waUrl={waUrl} />
            )}          </div>
        </main>
      </div>
    </div>
  );
}

/* ----- Reusable blocks ----- */
const CenteredStep = ({ eyebrow, title, subtitle, cta, onCta, ctaTestId, icon }) => (
  <div className="flex-1 flex flex-col">
    <div className="flex-1 flex flex-col items-center justify-center text-center">
      <div className="h-16 w-16 rounded-2xl bg-[var(--el-red)]/10 flex items-center justify-center">{icon}</div>
      {eyebrow && <div className="mt-4 text-[10px] font-bold tracking-[0.32em] uppercase text-slate-400">{eyebrow}</div>}
      <h1 className="mt-2 text-[28px] leading-[1.15] sm:text-3xl font-[Outfit] font-extrabold tracking-tight text-slate-900 max-w-[18rem]">{title}</h1>
      {subtitle && <p className="mt-2.5 text-sm text-slate-600 max-w-[20rem]">{subtitle}</p>}
    </div>
    <Button onClick={onCta}
      className="btn-primary h-12 w-full rounded-2xl text-base font-semibold gap-2 mt-4"
      data-testid={ctaTestId}>
      {cta}<ArrowRight className="w-4 h-4" />
    </Button>
  </div>
);

const QuestionStep = ({ eyebrow, title, subtitle, children, cta, onCta, disabled, ctaTestId }) => (
  <div className="flex-1 flex flex-col">
    <div>
      {eyebrow && <div className="text-[10px] font-bold tracking-[0.32em] uppercase text-slate-400">{eyebrow}</div>}
      <h1 className="mt-1.5 text-2xl sm:text-3xl font-[Outfit] font-extrabold tracking-tight text-slate-900 leading-[1.15]">{title}</h1>
      {subtitle && <p className="mt-1.5 text-sm text-slate-500">{subtitle}</p>}
    </div>
    <div className="flex-1 mt-5 min-h-0 overflow-auto -mx-1 px-1 option-stagger flex flex-col gap-2.5">
      {children}
    </div>
    <Button onClick={onCta} disabled={disabled}
      className="btn-primary h-12 w-full rounded-2xl text-base font-semibold gap-2 mt-4 disabled:opacity-50"
      data-testid={ctaTestId}>
      {cta}<ArrowRight className="w-4 h-4" />
    </Button>
  </div>
);

const OptionGrid = ({ options, selected, onSelect, multi, testidPrefix }) => (
  <>
    {options.map(({ value, icon: Icon, advanced }) => {
      const isOn = selected.includes(value);
      return (
        <div key={value} role="button" tabIndex={0}
          onClick={() => onSelect(value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(value); }
          }}
          data-testid={`${testidPrefix}-${slug(value)}`}
          className={`flex items-center gap-3 rounded-2xl border px-4 py-3.5 cursor-pointer select-none transition active:scale-[0.99] ${
            isOn ? "border-[var(--el-red)] bg-[var(--el-red)]/5" : "border-slate-200 bg-white hover:border-slate-300"
          }`}>
          <span className={`shrink-0 h-9 w-9 rounded-xl flex items-center justify-center ${
              isOn ? "bg-[var(--el-red)] text-white" : "bg-slate-100 text-slate-700"}`}>
            <Icon className="w-4 h-4" />
          </span>
          <span className="flex-1 font-medium text-slate-900 text-[15px] flex items-center gap-2 min-w-0">
            <span className="truncate">{value}</span>
            {advanced && (
              <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-slate-900 text-white">
                Avanzado
              </span>
            )}
          </span>
          <span className={`shrink-0 h-5 w-5 rounded-full border flex items-center justify-center ${
              isOn ? "bg-[var(--el-red)] border-[var(--el-red)] text-white" : "border-slate-300"}`}
            aria-hidden="true">
            {isOn && <Check className="w-3 h-3" />}
          </span>
        </div>
      );
    })}
    {multi && <p className="text-[11px] text-slate-400 px-1 pt-1">Selección múltiple.</p>}
  </>
);

const LoadingStep = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="step-loading">
    <div className="relative h-20 w-20">
      <div className="absolute inset-0 rounded-full border-4 border-slate-100" />
      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[var(--el-red)] ring-spin" />
    </div>
    <h2 className="mt-6 text-xl font-[Outfit] font-bold text-slate-900">Guardando tus respuestas...</h2>
    <p className="mt-1.5 text-sm text-slate-500 max-w-[18rem]">Un momento, estamos preparando tu propuesta.</p>
    <div className="mt-5 flex items-center gap-1.5">
      <span className="loader-dot h-2 w-2 rounded-full bg-[var(--el-red)]" />
      <span className="loader-dot h-2 w-2 rounded-full bg-[var(--el-red)]" />
      <span className="loader-dot h-2 w-2 rounded-full bg-[var(--el-red)]" />
    </div>
  </div>
);

const ResultStep = ({ data, onContinue }) => (
  <div className="flex-1 flex flex-col" data-testid="step-result">
    <div className="flex-1 flex flex-col items-center text-center justify-center">
      <div className="h-16 w-16 rounded-2xl bg-[var(--el-red)] text-white flex items-center justify-center">
        <Sparkles className="w-7 h-7" />
      </div>
      <div className="mt-4 text-[10px] font-bold tracking-[0.32em] uppercase text-slate-400">
        Resultado
      </div>
      <h1 className="mt-2 text-2xl sm:text-3xl font-[Outfit] font-extrabold tracking-tight text-slate-900 leading-[1.15] max-w-[22rem]">
        Estamos preparando un prototipo personalizado para tu negocio
      </h1>
      <p className="mt-3 text-sm text-slate-600 max-w-[22rem]">
        Nuestro equipo está trabajando en un diseño adaptado
        {data.business_type ? ` a tu ${data.business_type.toLowerCase()}` : ""}.
        Recibirás tu prototipo en pocos minutos.
      </p>

      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
        <span className="text-xs font-semibold text-emerald-700">
          Datos enviados al equipo
        </span>
      </div>
    </div>
    <Button onClick={onContinue}
      className="btn-primary h-12 w-full rounded-2xl text-base font-semibold gap-2 mt-4"
      data-testid="btn-result-continue">
      Ver planes<ArrowRight className="w-4 h-4" />
    </Button>
  </div>
);

const PlansStep = ({ selected, onSelect, onContinue }) => (
  <div className="flex-1 flex flex-col" data-testid="step-plans">
    <div className="text-[10px] font-bold tracking-[0.32em] uppercase text-slate-400">Planes</div>
    <h1 className="mt-1.5 text-2xl sm:text-3xl font-[Outfit] font-extrabold tracking-tight text-slate-900 leading-[1.15]">
      Elige tu plan
    </h1>
    <p className="mt-1.5 text-sm text-slate-500">Paga 50% ahora, 50% al entregar la web.</p>

    <div className="mt-4 flex-1 min-h-0 overflow-auto -mx-1 px-1 option-stagger flex flex-col gap-2.5">
      {PLANS.map((p) => {
        const active = selected === p.id;
        return (
          <div key={p.id} role="button" tabIndex={0}
            onClick={() => onSelect(p.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(p.id); } }}
            data-testid={`plan-${p.id}`}
            className={`relative rounded-2xl border p-4 cursor-pointer transition select-none ${
              active ? "border-[var(--el-red)] ring-2 ring-[var(--el-red)]/15 bg-[var(--el-red)]/5"
                     : "border-slate-200 bg-white hover:border-slate-300"
            } ${p.recommended ? "shadow-[0_8px_30px_rgba(155,34,38,0.10)]" : ""}`}>
            {p.recommended && (
              <span className="absolute -top-2 right-4 text-[10px] font-bold tracking-[0.18em] uppercase bg-[var(--el-red)] text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <Star className="w-3 h-3 fill-white" />Recomendado
              </span>
            )}
            {p.id === "gratuito" && !p.recommended && (
              <span className="absolute -top-2 left-4 text-[10px] font-bold tracking-[0.18em] uppercase bg-emerald-600 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <Gift className="w-3 h-3" />Sin pago
              </span>
            )}
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-[Outfit] font-bold text-lg text-slate-900">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.tagline}</div>
              </div>
              <div className="text-right">
                <div className="font-[Outfit] font-extrabold text-2xl text-slate-900 leading-none">{p.price}</div>
                <div className="text-[10px] text-slate-500 mt-1">{p.priceNote}</div>
              </div>
            </div>
            <ul className="mt-3 space-y-1.5">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-3.5 h-3.5 text-[var(--el-red)] mt-1 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5 flex items-start gap-3 mt-1">
        <Wrench className="w-4 h-4 text-slate-700 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          Todos los planes de pago incluyen <strong>revisión y mantenimiento trimestral</strong> para asegurar el correcto funcionamiento, actualización y mejoras continuas.
        </p>
      </div>
    </div>
    <Button onClick={onContinue}
      className="btn-primary h-12 w-full rounded-2xl text-base font-semibold gap-2 mt-4"
      data-testid="btn-plans-continue">
      Continuar<ArrowRight className="w-4 h-4" />
    </Button>
  </div>
);

const FinalStep = ({ data, setData, vendorValid, checkVendor, vendorFromRef, submitting, onSubmit, waUrl }) => {
  const plan = PLANS.find((p) => p.id === data.selected_plan) || PLANS[0];
  const isFree = data.selected_plan === "gratuito";
  return (
    <div className="flex-1 flex flex-col" data-testid="step-final">
      <div className="text-[10px] font-bold tracking-[0.32em] uppercase text-slate-400">Casi listo</div>
      <h1 className="mt-1.5 text-2xl sm:text-3xl font-[Outfit] font-extrabold tracking-tight text-slate-900 leading-[1.15]">
        Continúa con tu proyecto
      </h1>
      <p className="mt-1.5 text-sm text-slate-500">
        Plan elegido: <span className="font-semibold text-slate-800">{plan.name}</span>
        {!isFree && (
          <> · <span className="font-semibold text-[var(--el-red)]">{plan.priceNote}</span></>
        )}
      </p>

      <div className="mt-4 flex-1 min-h-0 overflow-auto -mx-1 px-1 flex flex-col gap-2.5 option-stagger">
        <Input placeholder="Tu nombre" value={data.contact_name}
          onChange={(e) => setData({ ...data, contact_name: e.target.value })}
          className="h-12 rounded-2xl" data-testid="input-final-name" />
        <Input type="email" placeholder="Email" value={data.email}
          onChange={(e) => setData({ ...data, email: e.target.value })}
          className="h-12 rounded-2xl" data-testid="input-final-email" />
        <Input placeholder="Teléfono (opcional)" value={data.phone}
          onChange={(e) => setData({ ...data, phone: e.target.value })}
          className="h-12 rounded-2xl" data-testid="input-final-phone" />

        <div className="pt-1">
          <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            Código de vendedor (opcional)
          </label>
          {vendorFromRef && vendorValid?.valid ? (
            <div
              className="mt-1 flex items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5"
              data-testid="vendor-from-ref"
            >
              <div className="min-w-0">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-700">
                  Te invita
                </div>
                <div className="font-semibold text-slate-900 text-sm truncate">
                  {vendorValid.name}{" "}
                  <span className="font-mono text-[var(--el-red)] tracking-wider">
                    {data.vendor_code}
                  </span>
                </div>
              </div>
              <Check className="w-4 h-4 text-emerald-600 shrink-0" />
            </div>
          ) : (
            <div className="relative mt-1">
              <Input placeholder="Ej. ELM0001" value={data.vendor_code}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase();
                  setData({ ...data, vendor_code: v });
                  checkVendor(v);
                }}
                className="h-11 rounded-2xl pr-20 tracking-widest font-semibold"
                data-testid="input-vendor-code" />
              {data.vendor_code && vendorValid && (
                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold ${
                  vendorValid.valid ? "text-emerald-600" : "text-red-600"
                }`}>
                  {vendorValid.valid ? vendorValid.name : "No válido"}
                </span>
              )}
            </div>
          )}
        </div>

        {!isFree && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 flex items-start gap-2.5 mt-1">
            <Shield className="w-4 h-4 text-emerald-600 mt-0.5" />
            <p className="text-xs text-slate-700 leading-relaxed">
              <strong>Pago seguro con Stripe.</strong> Puedes solicitar reembolso si no estás satisfecho.
            </p>
          </div>
        )}

        {/* Privacy / GDPR */}
        <label
          className="mt-1 flex items-start gap-2.5 cursor-pointer select-none"
          data-testid="terms-label"
        >
          <input
            type="checkbox"
            checked={data.terms_accepted}
            onChange={(e) => setData({ ...data, terms_accepted: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-[var(--el-red)] focus:ring-[var(--el-red)] cursor-pointer"
            data-testid="checkbox-terms"
          />
          <span className="text-xs text-slate-700 leading-relaxed">
            Acepto el uso de mis datos personales y la{" "}
            <a
              href="/privacidad"
              target="_blank"
              rel="noreferrer"
              className="text-[var(--el-red)] underline font-semibold"
              onClick={(e) => e.stopPropagation()}
              data-testid="link-privacy"
            >
              política de privacidad
            </a>
            .
          </span>
        </label>
      </div>

      <div className="mt-4 space-y-2.5">
        <Button onClick={() => onSubmit(!isFree)} disabled={submitting || !data.terms_accepted}
          className="btn-primary h-12 w-full rounded-2xl text-base font-semibold gap-2 disabled:opacity-50"
          data-testid="btn-final-submit">
          {isFree ? <Send className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
          {submitting ? "Procesando..." : (isFree ? "Enviar solicitud" : "Continuar y pagar")}
        </Button>
        <a href={waUrl} target="_blank" rel="noreferrer"
          className="h-11 w-full rounded-2xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition"
          data-testid="btn-final-whatsapp">
          <MessageCircle className="w-4 h-4" />Contactar por WhatsApp
        </a>
      </div>
    </div>
  );
};

/* ----- helpers ----- */
const slug = (s) => s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "")
  .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
